import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogIn, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";

const Login = () => {
  const { lang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("next") || "/account";

  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const t = lang === "fr" ? {
    kicker: "Compte Dataleo",
    title: "Se connecter",
    intro: "Accédez à votre compte pour suivre vos favoris, gérer votre profil et recevoir des synthèses personnalisées.",
    email: "Email professionnel",
    password: "Mot de passe",
    submit: "Se connecter",
    sending: "Connexion…",
    ok: "Connexion réussie. Redirection…",
    forgot: "Mot de passe oublié ?",
    noAccount: "Pas de compte ?",
    register: "Créer un compte",
  } : {
    kicker: "Dataleo account",
    title: "Sign in",
    intro: "Access your account to track favourites, manage your profile and receive tailored briefings.",
    email: "Business email",
    password: "Password",
    submit: "Sign in",
    sending: "Signing in…",
    ok: "Signed in. Redirecting…",
    forgot: "Forgot password?",
    noAccount: "No account?",
    register: "Create an account",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setOk(false);
    const res = await login(form.email.trim(), form.password);
    if (res.ok) {
      setOk(true);
      setTimeout(() => navigate(redirectTo, { replace: true }), 250);
    } else {
      setError(res.error || (lang === "fr" ? "Email ou mot de passe invalide." : "Invalid email or password."));
    }
    setBusy(false);
  };

  return (
    <div data-testid="login-page">
      <SeoHead lang={lang} path="/login" title={t.title} description={t.intro} noindex />
      <PageHero
        kicker={t.kicker}
        title={t.title}
        description={t.intro}
        accent="green"
        compact
        testId="login-hero"
      />
      <div className="dl-container py-10 max-w-md">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.email} *</span>
          <input
            data-testid="login-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.password} *</span>
          <input
            data-testid="login-password"
            type="password"
            required
            autoComplete="current-password"
            minLength={1}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>

        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-[#0f172a] disabled:opacity-60 text-white px-5 py-3 text-sm font-bold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} {busy ? t.sending : t.submit}
        </button>

        {error && (
          <div data-testid="login-error" className="inline-flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        {ok && (
          <div data-testid="login-ok" className="inline-flex items-center gap-2 text-sm text-[#00A878]">
            <CheckCircle2 size={16} /> {t.ok}
          </div>
        )}
      </form>

      <div className="mt-6 flex flex-col gap-2 text-sm text-[#4B5563]">
        <Link data-testid="login-forgot-link" to="/forgot-password" className="hover:text-[#00A878]">{t.forgot}</Link>
        <div>
          {t.noAccount}{" "}
          <Link data-testid="login-register-link" to={`/register${redirectTo && redirectTo !== "/account" ? `?next=${encodeURIComponent(redirectTo)}` : ""}`} className="font-semibold text-[#111827] hover:text-[#00A878]">
            {t.register}
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;
